import {
  formatPrice,
  IconQuestion,
  pluralize,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import { type GetStaticProps } from 'next';
import Image from 'next/image';
import { type FC } from 'react';

import { ReferralType } from '../__generated-gql-types__/globalTypes';
import ReferralsImageMobile from '../assets/images/referrals/referrals-hero-mobile.png';
import ReferralsImage from '../assets/images/referrals/referrals-hero.png';
import AuthedPageBase from '../components/AuthedPageBase';
import SvgIcon, { Svg } from '../components/SvgIcon';
import LexGuardedPage from '../components/page/LexGuardedPage';
import { type PageContentDataProps } from '../components/page/PageContent';
import { getStaticPageContentQuery } from '../components/page/PageContent';
import { LexSlug } from '../context/LexContext';
import { ServerTimeLogger } from '../utils/analytics/server-log-time';
import useCopyToClipboardWithToast from '../utils/hooks/useCopyToClipboardWithToast';
import { getCustomerReferralUrl } from '../utils/url';

import {
  useGetReferralSummaryQuery,
  useReferralsUserQuery,
} from './__generated-gql-types__/referrals.generated';

interface ReferralsPageProps {
  pageContentProps: PageContentDataProps;
}

const Referrals: FC<ReferralsPageProps> = ({ pageContentProps }) => {
  const { data } = useGetReferralSummaryQuery();
  const referralSummary = data?.referralSummary;
  const referralCount = referralSummary?.referralCount ?? 0;

  const { data: userData } = useReferralsUserQuery();
  const referralCode = userData?.user?.applicant?.referralCode;

  const copyLink = useCopyToClipboardWithToast();

  const shareableLink = getCustomerReferralUrl(
    referralCode ?? '',
    ReferralType.CUSTOMER_REFER_CUSTOMER,
  );

  interface DataItem {
    Svg: Svg;
    title: string;
    description: string;
  }
  const howItWorksCards: DataItem[] = [
    {
      Svg: Svg.REFERRAL_SHARE,
      title: 'Share your link',
      description:
        'Invite your friends to join Leland using your unique referral link',
    },
    {
      Svg: Svg.REFERRAL_FRIEND,
      title: 'Your friend gets $50',
      description:
        'When they sign up, they’ll receive $50 in Leland credit to use on coaching',
    },
    {
      Svg: Svg.REFERRAL_RECEIVE,
      title: 'You get $50',
      description:
        'Earn $50 in Leland credit when they make their first coaching purchase',
    },
  ];

  return (
    <LexGuardedPage lex={LexSlug.FEATURE_CUSTOMER_REFERRAL}>
      <AuthedPageBase
        title="Referral Page"
        fullWidth
        hideTitle
        initialData={pageContentProps}
      >
        <div className="relative bg-leland-primary-extra-light">
          <Image
            className="pointer-events-none absolute -bottom-24 object-contain object-bottom sm:-bottom-8 md:hidden"
            alt="Referrals background mobile"
            src={ReferralsImageMobile}
          />

          {/* Background Image Desktop */}
          <Image
            className="pointer-events-none absolute right-0 top-28 hidden h-full object-contain object-right md:block lg:top-0"
            alt="Referrals background"
            src={ReferralsImage}
          />
          {/* Hero */}
          <div className="relative mx-auto px-5 pb-10 pt-8 md:max-w-4xl md:pb-28 md:pt-14">
            <div className=" flex w-full flex-col justify-between gap-10 lg:flex-row">
              <div className="space-y-6 md:max-w-md">
                <h2 className="text-3xl font-medium md:text-4xl">
                  Give $50, Get $50
                </h2>
                <p className="text-lg text-leland-dark-green">
                  Know someone who could use Leland? When you refer your friends
                  to Leland and they purchase, you’ll both get $50 in Leland
                  credit.
                </p>
                <div className="space-y-1">
                  <h4 className="font-medium text-leland-dark-green">
                    Your unique referral link
                  </h4>
                  <div className="flex w-full items-start justify-between rounded-lg border border-leland-gray-stroke bg-white">
                    <div className="p-4">
                      <p className="line-clamp-1 break-all  text-leland-gray-extra-light">
                        {shareableLink} {/*Enforced at AuthedPageBased */}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="flex h-full items-center justify-center whitespace-nowrap p-4 text-leland-primary"
                      onClick={() => copyLink(shareableLink)}
                    >
                      Copy link
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div className="space-y-2.5 rounded-xl border bg-white p-5 shadow-xl lg:w-75">
                  <div className="flex items-center justify-between ">
                    <h4 className="text-leland-gray-light">
                      Total earnings from referrals
                    </h4>
                    <Tooltip
                      side="top"
                      content=" This number is your combined earnings from your referrals."
                    >
                      <IconQuestion className="size-4 text-leland-gray-extra-light" />
                    </Tooltip>
                  </div>
                  <p className="mb-2.5 text-3xl font-medium md:text-4xl">
                    {formatPrice(referralSummary?.referralEarnings ?? 0)}
                  </p>
                  <p className="text-leland-gray-extra-light">
                    {pluralize(referralCount, 'person has', {
                      pluralWord: 'people have',
                    })}{' '}
                    signed up via your link
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <section className="relative -mt-4 pb-12 md:-mt-14">
          <div className="m-auto max-w-4xl space-y-2 px-5 pb-12">
            <div className="flex flex-col gap-5 rounded-2xl border bg-white p-4 text-center text-lg lg:flex-row">
              {howItWorksCards.map(({ Svg, title, description }, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center justify-center space-y-4 py-5"
                >
                  <SvgIcon svgType={Svg} />

                  <div>
                    <h4 className="font-medium">{title}</h4>
                    <p className="text-leland-gray-light">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AuthedPageBase>
    </LexGuardedPage>
  );
};

export const getStaticProps: GetStaticProps<ReferralsPageProps> = async () => {
  const TimeLoggerClient = new ServerTimeLogger();

  const pageContentCategoriesHeaderData = await getStaticPageContentQuery();
  TimeLoggerClient.logTime('page content categories header');

  return {
    props: {
      pageContentProps: pageContentCategoriesHeaderData,
    },
    revalidate: 3600,
  };
};

export default Referrals;
